import { Row, Col, List, Avatar, Typography, Divider, Space } from "antd";
import { calculation } from "../data/calculationData";
import {
  AlignLeftOutlined,
  AuditOutlined,
  EuroOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { ClaimDossierCalculationActionsCard } from "./ClaimDossierCalculationActionsCard";
import { Claim } from "@rumble-it/common-lib/types/models/Claim.interface";
import { Calculation } from "@rumble-it/common-lib/types/models/Calculation.interface";
import { Card } from "@rumbledirect/ui";

const { Title, Text } = Typography;

export const ClaimDossierCalculationDetails = ({
  claim,
  calculation,
  setActiveTab,
}: {
  claim: Claim | null | undefined;
  calculation: Calculation | null | undefined;
  setActiveTab: (tab: string) => void;
}) => {
  return (
    <Row gutter={16}>
      <Col span={16}>
        {calculation?.calculationIntent === "Expertise" && <ExpertiseCard />}
        <PartsAndLabourCard />
        <PaintCard />
      </Col>

      <Col span={8}>
        <ClaimDossierCalculationActionsCard
          calculation={calculation}
          claim={claim}
          setActiveTab={setActiveTab}
        />
        <CostOverviewCard />
      </Col>
    </Row>
  );
};

const PartsAndLabourCard = () => (
  <Card
    title={
      <>
        <ToolOutlined /> <span className="ml-2">Onderdelen en arbeid</span>
      </>
    }
    className="mb-4"
  >
    <Typography.Title level={5}>Onderdelen</Typography.Title>
    <List
      itemLayout="horizontal"
      dataSource={calculation.parts}
      renderItem={(item) => (
        <List.Item>
          <List.Item.Meta
            avatar={
              <Avatar size={36} shape="square">
                {item.code}
              </Avatar>
            }
            title={
              <div className="flex justify-between items-center w-full">
                <div>
                  {item.desc} <Text type="secondary">• {item.partNo}</Text>
                </div>
                <div>
                  <Text strong>€ {item.price.toFixed(2)}</Text>
                </div>
              </div>
            }
            description={<Text type="secondary">Aantal: {item.qty}</Text>}
          />
        </List.Item>
      )}
    />

    <Divider />
    <Title level={5}>Arbeid</Title>
    <List
      dataSource={calculation.labor.breakdown}
      renderItem={(op) => (
        <List.Item>
          <List.Item.Meta
            title={
              <div className="flex justify-between items-center w-full">
                <div>
                  {op.op} <Text type="secondary">• {op.hours} hrs</Text>
                </div>
                <div>
                  <Text strong>€ {op.amount.toFixed(2)}</Text>
                </div>
              </div>
            }
          />
        </List.Item>
      )}
    />

    <Typography.Title level={5} className="mt-5">
      Overige kosten
    </Typography.Title>
    <List
      dataSource={calculation.additional}
      renderItem={(a) => (
        <List.Item>
          <List.Item.Meta
            title={
              <div className="flex justify-between items-center w-full">
                <div>{a.label}</div>
                <div>
                  <Text>€ {a.price.toFixed(2)}</Text>
                </div>
              </div>
            }
          />
        </List.Item>
      )}
    />
  </Card>
);

const PaintCard = () => (
  <Card
    title={
      <>
        <AuditOutlined /> <span className="ml-2">Spuitwerk</span>
      </>
    }
    className="mb-4"
  >
    <List
      dataSource={calculation.paint}
      renderItem={(op) => (
        <List.Item>
          <List.Item.Meta
            title={
              <div className="flex justify-between items-center w-full">
                <div>{op.op} </div>
                <div>
                  <Text strong>€ {op.amount.toFixed(2)}</Text>
                </div>
              </div>
            }
          />
        </List.Item>
      )}
    />
    <Divider />
    <div className="flex justify-end">
      <Text strong>Subtotaal Spuitwerk: € 616.08</Text>
    </div>
  </Card>
);

const ExpertiseCard = () => (
  <Card
    title={
      <>
        <AlignLeftOutlined /> <span className="ml-2">Expertise rapport</span>
      </>
    }
    className="mb-4"
  >
    <Text>
      Op basis van de uitgevoerde opname en bijbehorende calculatie stel ik vast
      dat het voertuig schade heeft opgelopen. De schade omvat onder meer
      vervormingen aan de voorbumper, voorscherm links, motorkap en koplamp
      links. Daarnaast zijn er spuitwerkzaamheden noodzakelijk en dienen diverse
      bevestigingsmaterialen te worden vervangen.
    </Text>
    <br />
    <br />
    <Text>
      De totale herstelkosten bedragen volgens de calculatie € 1831,22 exclusief
      BTW. De dagwaarde van het voertuig op peildatum bedraagt circa € 12.000,–.
      Op basis hiervan concludeer ik dat het voertuig technisch en economisch
      herstelbaar is. Er is geen sprake van een economisch totaalverlies.
    </Text>
    <br />
    <br />
    <Text>
      Het herstel kan op een doelmatige wijze worden uitgevoerd door een erkend
      schadeherstelbedrijf. Tijdens de opname zijn geen aanwijzingen gevonden
      voor aanvullende verborgen schade, maar gezien de aard van de impact kan
      bij demontage nog geringe meer- of minderwerk worden vastgesteld.
    </Text>
    <br />
    <br />
    <Text>
      Ik adviseer om het voertuig volledig te laten herstellen conform de
      opgestelde calculatie. Na herstel zal de verkeersveiligheid hersteld zijn
      en is de bruikbaarheid van het voertuig weer conform de oorspronkelijke
      staat. Eventuele waardevermindering na herstel is beperkt en ligt binnen
      de gebruikelijke bandbreedte voor voertuigen in deze leeftijdsklasse.
    </Text>
  </Card>
);

const CostOverviewCard = () => (
  <Card
    className="mb-4"
    title={
      <>
        <EuroOutlined /> <span className="ml-2">Kostenoverzicht</span>
      </>
    }
  >
    <Space direction="vertical" style={{ width: "100%" }}>
      <div className="flex justify-between">
        <Text>Onderdelen</Text>
        <Text>€ {calculation.totals.parts.toFixed(2)}</Text>
      </div>
      <div className="flex justify-between">
        <Text>Arbeid</Text>
        <Text>€ {calculation.totals.labor.toFixed(2)}</Text>
      </div>
      <div className="flex justify-between">
        <Text>Overige Kosten</Text>
        <Text>€ {calculation.totals.additional.toFixed(2)}</Text>
      </div>
      <div className="flex justify-between">
        <Text>Spuitwerk</Text>
        <Text>€ {calculation.totals.paint.toFixed(2)}</Text>
      </div>

      <Divider />

      <div className="flex justify-between">
        <Text strong>Subtotal (excl. BTW)</Text>
        <Text strong>€ {calculation.totals.exclVat.toFixed(2)}</Text>
      </div>
      <div className="flex justify-between">
        <Text>BTW (21%)</Text>
        <Text>€ {calculation.totals.vat.toFixed(2)}</Text>
      </div>

      <Divider />

      <div className="flex justify-between text-lg">
        <Title level={4} className="!my-0">
          Total
        </Title>
        <Title level={4} className="!my-0">
          € {calculation.totals.inclVat.toFixed(2)}
        </Title>
      </div>
    </Space>
  </Card>
);
